import React, { Fragment } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import invert from 'lodash/invert';
import pickBy from 'lodash/pickBy';

import {
  BrowserRouter, Switch, Route, withRouter,
} from 'react-router-dom';
import Select from 'react-select';
import styled from 'styled-components';

import SegmentClient from '../services/SegmentClient';
import {
  BASE_URL,
  SORTINGS,
  OPERATION,
  filtersMap,
  emptyFilterMessage,
  DEFAULT_FIRM_TYPE,
  ALL_FIRM_NAMES,
  ALL_PROJECT_TYPES,
  ALL_PROJECT_LOCATIONS,
  ALL_FIRM_LOCATIONS,
  ANALYTICS_NAME,
  DEFAULT_FILLTERS_VALUES,
  FIRM_TYPE_PLURALIZATION,
  FIRM_TYPE_FILTER_KEY,
  FIRM_NAME_FILTER_KEY,
  PROJECT_TYPE_FILTER_KEY,
  PROJECT_LOCATION_FILTER_KEY,
  FIRM_LOCATION_FILTER_KEY,
} from '../components/firm_search/Constants';
import APIClient, { iterUnpack, architizerLogoUrl } from '../services/APIClient';
import Firm from '../components/firm_search/Firm';
import {
  prepareOptionAsGqlParameter,
  prepareOptions,
  prepareOption,
  pluralizeLastWord,
  pluralizeLastWordArray,
  numberWithCommas,
  architizerHistoryListener,
} from '../services/utils';
import { UserResource } from '../services/RESTClient';
import RequireRegisterPopup, { PERMISSION_TYPES } from '../components/RequireRegisterPopup';
import {
  ANALYTICS,
  HTTP_METHODS,
} from '../services/constants';
import GA4Client from '../services/GA4Client';

const ga4 = new GA4Client();

const userResource = new UserResource();

const PoweredByArchitizer = styled.a`
  && {
    position: fixed;
    left: 10px;
    bottom: 24px;
    z-index: 1;
    padding: 4px 10px;
    border-radius: 2px;
    background-color: rgba(255, 255, 255, .75);

    &:hover {
      background-color: rgba(255, 255, 255, .90);
    }
  }
`;

const HeaderFilter = styled.strong`
  border-bottom: 1px dashed;
`;

class FirmSearch extends React.Component {
  constructor(props) {
    super(props);
    const { params } = props.match;
    const data = iterUnpack(JSON.parse(window.props));
    const firmNames = [];
    const firmTypes = this.prepareFirmTypes(data.firmTypes);
    const projectTypes = prepareOptions(data.projectTypes);
    const projectLocations = prepareOptions(data.projectLocations);
    const firmLocations = prepareOptions(data.firmLocations);
    this.userId = data.userId;
    this.segment = new SegmentClient();

    this.state = {
      ...this.defaultState(),
      ...data,
      affiliationSlug: params.affiliationSlug || '',
      isAffiliationDirectory: Boolean(params.affiliationSlug),
      firmNames,
      firmTypes,
      projectTypes,
      projectLocations,
      firmLocations,
      filters: this.getFiltersFromPath(params.filters),
      firms: (data.firms || []).filter(e => e),
    };
  }

  componentDidMount() {
    this.updateDocumentMeta();
    if (this.numberOfCurrentNotDefaultFilters()) {
      this.getFirms(OPERATION.LOAD_NEW, false);
    } else {
      setTimeout(() => { this.sendAnalytics(ANALYTICS_NAME.SEARCH_SUBMITTED); });
    }

    this.props.history.listen(architizerHistoryListener((
      location,
      action,
    ) => {
      const { params } = this.props.match;
      if (action === 'POP') {
        this.setState({ filters: this.getFiltersFromPath(params.filters) }, () => {
          this.getFirms(OPERATION.LOAD_NEW, false);
        });
      }
    }));

    userResource.me()
      .finally(() => this.setState({
        canFindSimilarImages: userResource.canFindSimilar(),
        canCollect: userResource.canCollect(),
      }));
  }

  onFilterChange = (value, filter) => {
    this.setState({
      filters: {
        ...this.state.filters,
        [filter]: value,
      },
    }, () => {
      this.getFirms(OPERATION.LOAD_NEW);
    });
  };

  getFiltersFromPath = (path) => {
    // Split by '/' to get all filters
    const params = (path || '').split('/');
    const filters = {};
    params.forEach((param) => {
      // Split by '=' to get filter name and value
      const filterStr = param.split('=');
      const name = filtersMap[filterStr[0]];
      const value = filterStr[1] || '';
      if (name && value) {
        const decodedValue = decodeURIComponent(value);
        // Handle sort filter
        if (name === filtersMap.sort) {
          if (decodedValue === SORTINGS[0].value) {
            filters[name] = SORTINGS[0];
          } else if (decodedValue === SORTINGS[1].value) {
            filters[name] = SORTINGS[1];
          } else if (decodedValue === SORTINGS[2].value) {
            filters[name] = SORTINGS[2];
          } else if (decodedValue === SORTINGS[3].value) {
            filters[name] = SORTINGS[3];
          } else if (decodedValue === SORTINGS[4].value) {
            filters[name] = SORTINGS[4];
          }
        // Handle other filters
        } else {
          filters[name] = prepareOption(decodedValue);
        }
      }
    });

    return this.getInitFilters(filters);
  };

  getHeader = (isDecorated = false) => {
    const {
      filters: {
        selectedFirmType,
        selectedFirmName,
        selectedProjectType,
        selectedProjectLocation,
        selectedFirmLocation,
      },
      totalCount,
    } = this.state;

    const projectType = !isEqual(selectedProjectType, ALL_PROJECT_TYPES)
      ? ` ${selectedProjectType.value}`
      : '';
    const decoratedProjectType = projectType ? (
      <><HeaderFilter>{selectedProjectType.value}</HeaderFilter></>
    ) : '';

    const projectLocation = !isEqual(selectedProjectLocation, ALL_PROJECT_LOCATIONS)
      ? ` With Projects in ${selectedProjectLocation.value}`
      : '';
    const decoratedProjectLocation = projectLocation ? (
      <> With Projects in <HeaderFilter>{selectedProjectLocation.value}</HeaderFilter></>
    ) : '';

    const firmLocation = !isEqual(selectedFirmLocation, ALL_FIRM_LOCATIONS)
      ? ` in ${selectedFirmLocation.value}`
      : '';
    const decoratedFirmLocation = firmLocation ? (
      <> in <HeaderFilter>{selectedFirmLocation.value}</HeaderFilter></>
    ) : '';

    const firmType = selectedFirmType
      ? ` ${pluralizeLastWord(selectedFirmType.value)}`
      : DEFAULT_FIRM_TYPE.label;
    const decoratedFirmType = firmType ? (
      <> <HeaderFilter>{pluralizeLastWord(selectedFirmType.value)}</HeaderFilter></>
    ) : '';

    const firmName = !isEqual(selectedFirmName, ALL_FIRM_NAMES) ? selectedFirmName.value : '';

    const prefix = `Displaying ${numberWithCommas(totalCount)}`;

    if (firmName) {
      return isDecorated
        ? (
          <>
            Looking For&nbsp;
            <HeaderFilter>{firmName}</HeaderFilter>
          </>
        )
        : `Looking For ${firmName}`;
    } if (this.areDefaultFiltersWithoutSorting()) {
      // All filters are default
      return `${prefix} Firms`;
    }

    return isDecorated
      ? (
        <>
          {prefix}
          {decoratedProjectType}
          {decoratedFirmType}
          {decoratedFirmLocation}
          {decoratedProjectLocation}
        </>
      )
      : `${prefix}${projectType}${firmType}${firmLocation}${projectLocation}`;
  };

  getFirms = (opType = OPERATION.LOAD_MORE, saveFilters = true) => {
    if (this.state.isLoading) {
      return;
    }
    this.setState({ isLoading: true });
    const {
      affiliationSlug,
      isAffiliationDirectory,
      filters: {
        selectedSort,
        selectedFirmType,
        selectedFirmName,
        selectedProjectType,
        selectedProjectLocation,
        selectedFirmLocation,
      },
    } = this.state;
    let filters = '';

    const endCursor = opType === OPERATION.LOAD_NEW ? '0' : this.state.metadata.allFirmsWithProjects.endCursor;

    if (opType === OPERATION.LOAD_NEW) {
      this.setState({ firms: [] });
      filters = `
      firmTypes
      projectTypes`;
      setTimeout(() => {
        this.sendAnalytics(ANALYTICS_NAME.SEARCH_SUBMITTED);
      }, 0);
      this.updateDocumentMeta();
    }
    if (saveFilters) {
      this.pushHistory(this.buildFiltersPath());
    }

    const query = `{
      allFirmsWithProjects(
        first: 10,
        after: "${endCursor}",
        ${selectedFirmType ? `firmType: ${prepareOptionAsGqlParameter(selectedFirmType)}` : ''},
        ${selectedFirmName ? `firmName: ${prepareOptionAsGqlParameter(selectedFirmName)}` : ''},
        projectType: ${prepareOptionAsGqlParameter(selectedProjectType)},
        projectLocation: ${prepareOptionAsGqlParameter(selectedProjectLocation)},
        firmLocation: ${prepareOptionAsGqlParameter(selectedFirmLocation)},
        orderBy: ${prepareOptionAsGqlParameter(selectedSort)},
        affiliationSlug: "${affiliationSlug}",
      ) {
        firms: edges {
          cursor
          node {
            index
            id: firmId
            slug: firmSlug
            name: firmName
            projectsCount: firmProjectsCount
            lastProjectDate: firmLastProjectDate
            media: firmLogoUrl
            showAPlusBadge: firmShowAPlusBadge
            projects {
              edges {
                node {
                  slug: slug
                  media: heroUrl
                  mediaId: heroId
                  isHiddenFromListings
                }
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        totalCount
        ${filters}
      }
    }`;

    // GET is used for affiliation directories because of Safari Full Third-Party Cookie Blocking
    // which makes APIClient unable to send cookies to the server, thus, it results in a 403.
    // https://webkit.org/blog/10218/full-third-party-cookie-blocking-and-more/
    const client = isAffiliationDirectory
      ? APIClient(query, true, null, HTTP_METHODS.GET)
      : APIClient(query, true);

    client.then((data) => {
      const updatedState = {
        firms: [
          ...(opType === OPERATION.LOAD_MORE ? this.state.firms : []),
          ...(data.allFirmsWithProjects.firms.filter(e => e).map(e => e.node)),
        ],
        isLoading: false,
        metadata: data.metadata,
        totalCount: data.allFirmsWithProjects.totalCount,
      };
      if (opType === OPERATION.LOAD_NEW) {
        updatedState.firmName = [ALL_FIRM_NAMES];
        updatedState.firmTypes = this.prepareFirmTypes(data.allFirmsWithProjects.firmTypes);
        updatedState.projectTypes = prepareOptions(data.allFirmsWithProjects.projectTypes);
        updatedState.projectLocations = [ALL_PROJECT_LOCATIONS];
        updatedState.firmLocations = [ALL_FIRM_LOCATIONS];
      }
      this.setState(updatedState);
    });
  };

  getInitFilters = (overrideFilters = {}) => {
    const filters = {
      [filtersMap.sort]: SORTINGS[0],
      [filtersMap[FIRM_NAME_FILTER_KEY]]: ALL_FIRM_NAMES,
      [filtersMap[FIRM_TYPE_FILTER_KEY]]: DEFAULT_FIRM_TYPE,
      [filtersMap[PROJECT_TYPE_FILTER_KEY]]: ALL_PROJECT_TYPES,
      [filtersMap[PROJECT_LOCATION_FILTER_KEY]]: ALL_PROJECT_LOCATIONS,
      [filtersMap[FIRM_LOCATION_FILTER_KEY]]: ALL_FIRM_LOCATIONS,
    };
    return Object.assign({}, filters, overrideFilters);
  };

  getFilterOptions = (input, callback, field = '') => {
    if (!input && ['firmNames', 'firmLocations', 'projectLocations'].includes(field)) {
      return callback();
    }
    const {
      affiliationSlug,
      isAffiliationDirectory,
      filters: {
        selectedFirmType,
        selectedFirmName,
        selectedProjectType,
        selectedProjectLocation,
        selectedFirmLocation,
      },
    } = this.state;

    const query = `{
      allFirmsWithProjects(
        first: 0,
        input: "${encodeURIComponent(input)}",
        ${selectedFirmType ? `firmType: ${prepareOptionAsGqlParameter(selectedFirmType)}` : ''},
        ${selectedFirmName ? `firmName: ${prepareOptionAsGqlParameter(selectedFirmName)}` : ''},
        projectType: ${prepareOptionAsGqlParameter(selectedProjectType)},
        projectLocation: ${prepareOptionAsGqlParameter(selectedProjectLocation)},
        firmLocation: ${prepareOptionAsGqlParameter(selectedFirmLocation)},
        affiliationSlug: "${affiliationSlug}",
      ) {
        ${field}
      }
    }`;

    const client = isAffiliationDirectory
      ? APIClient(query, true, null, HTTP_METHODS.GET)
      : APIClient(query, true);
    return client
      .then(data => prepareOptions(data.allFirmsWithProjects[field]))
      .then(options => callback(null, { options }));
  };

  prepareFirmTypes = firmTypes => pluralizeLastWordArray(
    firmTypes,
    FIRM_TYPE_PLURALIZATION.ignore,
    FIRM_TYPE_PLURALIZATION.convert,
  ).map((pluralizedType, i) => prepareOption(pluralizedType, firmTypes[i], {
    type: FIRM_TYPE_FILTER_KEY,
  }));

  isDefaultFilter = filter => ['', ...DEFAULT_FILLTERS_VALUES].includes(filter);

  areDefaultFiltersWithoutSorting = () => {
    let areDefaults = true;
    [
      this.state.filters.selectedFirmType,
      this.state.filters.selectedProjectType,
      this.state.filters.selectedProjectLocation,
      this.state.filters.selectedFirmLocation,
    ].forEach((e) => {
      areDefaults = this.isDefaultFilter(e.value) ? areDefaults : false;
    });

    return areDefaults;
  };

  updateDocumentMeta = () => {
    const headerContent = this.getHeader();
    document.title = `${headerContent} - Architizer`;
    document.querySelector('meta[name="description"]').setAttribute('content', String(headerContent));
  };

  buildFiltersPath = () => Object.keys(this.state.filters).map((key) => {
    const { filters } = this.state;
    // Don't include default filters in the generated path
    if (filters[key] && !this.isDefaultFilter(filters[key].value)) {
      const value = encodeURIComponent(filters[key].value);
      return value && `${invert(filtersMap)[key]}=${value}`;
    }
    return '';
  }).filter(e => e).join('/');

  sendAnalytics = (name, props = {}) => {
    const { filters } = this.state;
    const parsedFilters = {};
    const selectedFilters = pickBy(filters, v => (v && v.value !== ALL_FIRM_NAMES.value
        && v.value !== ALL_FIRM_LOCATIONS.value && v.value !== ALL_PROJECT_LOCATIONS.value
        && v.value !== ALL_PROJECT_TYPES.value));
    Object.keys(selectedFilters).forEach((k) => {
      parsedFilters[invert(filtersMap)[k]] = selectedFilters[k].value;
    });
    this.segment.track(
      name,
      Object.assign(
        {
          userId: this.userId,
        },
        parsedFilters,
        { ...props },
      ),
    );
  };

  numberOfCurrentNotDefaultFilters = () => Object.keys(this.state.filters).reduce(
    (acc, k) => {
      const filter = this.state.filters[k];
      const r = acc + (this.isDefaultFilter(filter ? filter.value : '') ? 0 : 1);
      return r;
    }, 0,
  );

  pushHistory = (filtersPath) => {
    const { isAffiliationDirectory, affiliationSlug } = this.state;
    this.props.history.push(`/${BASE_URL}/${isAffiliationDirectory ? `embed/${affiliationSlug}/` : ''}${filtersPath}`);
    this.segment.page();
    ga4.page();
  };

  openRequireRegisterPopup = ({
    permission, id, requireRegisterNext, destinationTitle,
  }) => {
    this.setState({
      requireRegisterPermission: permission,
      openPopupForMiaId: id,
      requireRegisterNext,
      requireRegisterDestinationTitle: destinationTitle,
    });
  }

  defaultState = () => ({
    userId: null,
    affiliationSlug: '',
    isAffiliationDirectory: false,
    isLoading: false,
    openPopupForMiaId: null,
    requireRegisterPermission: null,
    requireRegisterNext: null,
    requireRegisterDestinationTitle: null,
    canFindSimilarImages: false,
    canCollect: false,
    metadata: {
      allFirmsWithProjects: {
        endCursor: '0',
        hasNextPage: false,
      },
    },
    firms: [],
    firmTypes: [],
    firmNames: [],
    projectTypes: [],
    projectLocations: [],
    firmLocations: [],
    filters: this.getInitFilters(),
    totalCount: 0,
  });

  render() {
    const {
      isLoading,
      isAffiliationDirectory,
      firmNames,
      firmTypes,
      projectTypes,
      projectLocations,
      firmLocations,
      firms,
      filters: {
        selectedSort,
        selectedFirmType,
        selectedFirmName,
        selectedProjectType,
        selectedProjectLocation,
        selectedFirmLocation,
      },
    } = this.state;
    const sortOptions = SORTINGS.filter(s => s !== SORTINGS[4] || isAffiliationDirectory);

    return (
      <>
        {isAffiliationDirectory && (
          <PoweredByArchitizer href="/" target="_blank" className="black">
            Powered by <img src={architizerLogoUrl.black.sm} width="86px" alt="Powered by Architizer" />
          </PoweredByArchitizer>
        )}
        {/* Filters */}
        <div className="row bg-extra-light-gray align-center pt-base pb-xl" style={{ boxShadow: '0 0 4px rgba(25,25,25,0.1)' }}>
          <div className="column small-12 medium-12 large-7 xlarge-6 gutter-xxl small-gutter-xs">
            <p className="m-0 fw-bold fs-m gunmetal-900 pt-base">Filter Firms By</p>
            <div className="row">
              {/* Firm Name */}
              <div className="columns small-12 medium-4 large-4">
                <label htmlFor="firm-name-selector">Name</label>
                <Select.Async
                  name="firm-name-selector"
                  className="large border-gray-200 br-s"
                  value={selectedFirmName}
                  loadOptions={(input, callback) => this.getFilterOptions(input, callback, 'firmNames')}
                  onChange={value => this.onFilterChange(value, 'selectedFirmName')}
                  autoload={false}
                  cache={false}
                  options={firmNames}
                  disabled={isLoading}
                  tabSelectsValue={false}
                  deleteRemoves={false}
                  backspaceRemoves={false}
                  resetValue={ALL_FIRM_NAMES}
                  noResultsText={emptyFilterMessage}
                  ignoreAccents={false}
                />
              </div>
              {/* Firm Type */}
              <div className="columns small-12 medium-4 large-4">
                <label htmlFor="firm-type-selector">Type</label>
                <Select
                  name="firm-type-selector"
                  className="large border-gray-200 br-s"
                  onChange={value => this.onFilterChange(value, 'selectedFirmType')}
                  value={selectedFirmType}
                  options={firmTypes}
                  disabled={isLoading}
                  tabSelectsValue={false}
                  deleteRemoves={false}
                  backspaceRemoves={false}
                  resetValue={firmTypes[0]}
                  ignoreAccents={false}
                />
              </div>
              {/* Firm Location */}
              <div className="columns small-12 medium-4 large-4">
                <label htmlFor="project-firm-selector">Location</label>
                <Select.Async
                  name="firm-location-selector"
                  className="large border-gray-200 br-s"
                  onChange={value => this.onFilterChange(value, 'selectedFirmLocation')}
                  value={selectedFirmLocation}
                  loadOptions={(input, callback) => this.getFilterOptions(input, callback, 'firmLocations')}
                  autoload={false}
                  cache={false}
                  options={firmLocations}
                  disabled={isLoading}
                  tabSelectsValue={false}
                  deleteRemoves={false}
                  backspaceRemoves={false}
                  resetValue={ALL_FIRM_LOCATIONS}
                  noResultsText={emptyFilterMessage}
                  ignoreAccents={false}
                />
              </div>
            </div>
          </div>
          <div className="column small-12 medium-12 large-5 xlarge-4 gutter-xxl small-gutter-xs">
            <p className="m-0 fw-bold fs-m gunmetal-900 pt-base">Filter Projects By</p>
            <div className="row">
              {/* Project Type */}
              <div className="columns small-12 medium-6 large-6">
                <label htmlFor="project-type-selector">Type</label>
                <Select
                  name="project-type-selector"
                  className="large border-gray-200 br-s"
                  onChange={value => this.onFilterChange(value, 'selectedProjectType')}
                  value={selectedProjectType}
                  options={projectTypes}
                  disabled={isLoading}
                  tabSelectsValue={false}
                  deleteRemoves={false}
                  backspaceRemoves={false}
                  resetValue={projectTypes[0]}
                  ignoreAccents={false}
                />
              </div>
              {/* Project Location */}
              <div className="columns small-12 medium-6 large-6">
                <label htmlFor="project-location-selector">Location</label>
                <Select.Async
                  name="project-location-selector"
                  className="large border-gray-200 br-s"
                  onChange={value => this.onFilterChange(value, 'selectedProjectLocation')}
                  value={selectedProjectLocation}
                  loadOptions={(input, callback) => this.getFilterOptions(input, callback, 'projectLocations')}
                  autoload={false}
                  cache={false}
                  options={projectLocations}
                  disabled={isLoading}
                  tabSelectsValue={false}
                  deleteRemoves={false}
                  backspaceRemoves={false}
                  resetValue={ALL_PROJECT_LOCATIONS}
                  noResultsText={emptyFilterMessage}
                  ignoreAccents={false}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="row align-center">
          <div className="columns gutter-xxl small-gutter-xs pt-base pb-base">
            {/* Title */}
            <h1 className="fs-l mb-base">{this.getHeader(true)}</h1>
            {/* Sorting */}
            <div>
              <span>Sort By: </span>
              {sortOptions.map((sort, i) => (
                <Fragment key={sort.value}>
                  <a
                    className={`${isEqual(selectedSort, sort) ? 'blue underline' : 'black'}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => this.onFilterChange(sort, filtersMap.sort)}
                  >
                    {sort.label}
                  </a>
                  {i + 1 < sortOptions.length && <span> • </span>}
                </Fragment>
              ))}
            </div>
            <hr className="columns small-12 mt-base border-global-separator" />
          </div>
        </div>

        {/* Firm Results */}
        <div className="row">
          <div className="column gutter-xxl small-gutter-xs mb-base">
            <div className="row align-center">
              {firms.map((firm, i) => (
                <Firm
                  key={firm.index}
                  firm={firm}
                  showImageActions={!isAffiliationDirectory}
                  openInNewTab={isAffiliationDirectory}
                  canFindSimilarImages={this.state.canFindSimilarImages}
                  canCollect={this.state.canCollect}
                  trackFindSimilarImagesClick={imageUrl => this.segment.track(
                    ANALYTICS.FIND_SIMILAR_ACTION, {
                      origin_page: window.location.href,
                      image_url: imageUrl,
                      company_name: firm.name,
                      company_id: firm.id,
                    },
                  )}
                  openRequireRegisterPopup={this.openRequireRegisterPopup}
                  hasDivider={i + 1 < firms.length}
                  sendAnalytics={this.sendAnalytics}
                />
              ))}
              {!firms.length && !this.state.isLoading
                ? (
                  <p className="text-center" style={{ padding: '50px 0 200px 0' }}>
                    No firms found, try a different set of search terms.
                  </p>
                )
                : <></>}
              {this.state.isLoading ? <div className="loading-spinner-m m-auto" key={0} /> : <></>}
            </div>
          </div>
        </div>

        {firms.length
          && this.state.metadata.allFirmsWithProjects
          && this.state.metadata.allFirmsWithProjects.hasNextPage
          && !this.state.isLoading
          ? (
            <div className="flex-container align-center">
              <button
                type="button"
                className="button"
                style={{ margin: '40px auto', padding: '10px 15px' }}
                onClick={() => this.getFirms(OPERATION.LOAD_MORE, false)}
              >
                Load More&hellip;
              </button>
            </div>
          )
          : <></>}

        {this.state.openPopupForMiaId && (
          <RequireRegisterPopup
            permissionType={this.state.requireRegisterPermission}
            permissionDestinationTitle={this.state.requireRegisterDestinationTitle}
            joinCompany={this.state.requireRegisterPermission === PERMISSION_TYPES.FIND_SIMILAR}
            submitProfessional
            onClose={() => this.setState({
              openPopupForMiaId: null,
              requireRegisterPermission: null,
              requireRegisterDestinationTitle: null,
              requireRegisterNext: null,
            })}
            nextParam={this.state.requireRegisterNext}
          />
        )}
      </>
    );
  }
}

FirmSearch.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      affiliationSlug: PropTypes.string,
      filters: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    location: PropTypes.shape({}).isRequired,
    listen: PropTypes.func,
    push: PropTypes.func,
  }).isRequired,
};

ReactDOM.render(
  <BrowserRouter>
    <Switch>
      <Route
        path="/firms/embed/:affiliationSlug([-\w\d_]+)/:filters(.*)*"
        component={withRouter(FirmSearch)}
      />
      <Route
        path="/firms/:filters(.*)*"
        component={withRouter(FirmSearch)}
      />
    </Switch>
  </BrowserRouter>,
  document.getElementById('firm-search-root'),
);
